/**
 * KAN-753: менеджер сессии кабинета.
 *
 * - available: пользователь известен и у организации есть активные менеджеры
 *   (блок "Требуют внимания" и кнопка "Отработано" доступны любому вошедшему
 *   пользователю, как и сам API: IsAuthenticated);
 * - showPrompt: окно выбора после входа ещё не показывалось;
 * - manager: выбранный менеджер, если он есть в текущем списке. Удалённый
 *   (или ушедший в архив) менеджер сбрасывается в "не выбран".
 * Выбор хранится в localStorage (lib/sessionManager) до выхода из кабинета.
 * KAN-811: вошедший менеджер (роль MANAGER) сам и есть менеджер сессии:
 * окна нет, выбрать другого нельзя (locked), справочник не нужен.
 */
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { isManagerUser } from '@/lib/managerAccess';
import { useManagers } from '@/hooks/useManagers';
import {
  parseSessionManagerState,
  readSessionManagerRaw,
  resolveSessionManager,
  subscribeSessionManager,
  writeSessionManager,
} from '@/lib/sessionManager';
import type { Manager, ManagerBrief } from '@/types/manager';

export interface SessionManager {
  available: boolean;
  showPrompt: boolean;
  manager: ManagerBrief | null;
  managers: Manager[];
  /** Выбрать менеджера (null: снять выбор); окно больше не показывается. */
  select: (managerId: string | null) => void;
  /** "Пропустить": закрыть окно без выбора. */
  skip: () => void;
  /** KAN-811: менеджер сессии задан входом (роль MANAGER), выбор недоступен. */
  locked: boolean;
}

const NO_MANAGERS: Manager[] = [];
const noop = () => {};

export function useSessionManager(): SessionManager {
  const { data: user } = useCurrentUser();
  const userId = user?.id ?? null;
  const { data, isSuccess } = useManagers('true');
  const managers = data ?? NO_MANAGERS;

  const getSnapshot = useCallback(() => (userId ? readSessionManagerRaw(userId) : null), [userId]);
  const raw = useSyncExternalStore(subscribeSessionManager, getSnapshot, getSnapshot);
  const state = useMemo(() => parseSessionManagerState(raw), [raw]);

  const manager = isSuccess ? resolveSessionManager(state.managerId, managers) : null;
  const stale = isSuccess && state.managerId !== null && manager === null;

  useEffect(() => {
    if (userId && stale) writeSessionManager(userId, { ...state, managerId: null });
  }, [userId, stale, state]);

  const available = userId !== null && isSuccess && managers.length > 0;
  const self = useMemo<ManagerBrief | null>(
    () =>
      isManagerUser(user) && user?.manager_id
        ? { id: user.manager_id, full_name: user.manager_name ?? '' }
        : null,
    [user]
  );

  const select = useCallback(
    (managerId: string | null) => {
      if (userId) writeSessionManager(userId, { managerId, prompted: true });
    },
    [userId]
  );
  const skip = useCallback(() => {
    if (userId) writeSessionManager(userId, { ...state, prompted: true });
  }, [userId, state]);

  if (self) {
    return {
      available: true,
      showPrompt: false,
      manager: self,
      managers,
      select: noop,
      skip: noop,
      locked: true,
    };
  }

  return {
    available,
    showPrompt: available && !state.prompted,
    manager,
    managers,
    select,
    skip,
    locked: false,
  };
}
